import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService, StateService } from '@cosmos/angular/utils';
import { Project } from '@cosmos/shared';
import { environment } from 'environments/environment';
import { catchError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    //@InjectModel(File.name) private readonly fileModel: Model<File>,
    private http: HttpClient,
    private errorService: ErrorService,
    private stateService: StateService
  ) {}

  uploadFile(file: File[], project: Project, context: string) {
    const formData = new FormData();

    Array.from(file).forEach((element) => {
      formData.append('file', element);
      formData.append('filename', element.name);
      formData.append('context', context);
      formData.append('company', project.company as string);
    });

    return this.http
      .post(
        `${environment.API_URL}` + '/analytics/upload/' + project._id,
        formData,
        { reportProgress: true, observe: 'events' }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getProcessingJobs(query = '') {
    return this.http
      .get<Number | Object[]>(`${environment.API_URL}/analytics/processing-jobs${query}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  findProcessingJobById(id: string, query = '') {
    return this.http
      .get(`${environment.API_URL}/analytics/processing-jobs/${id}${query}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getProcessingJobImageBlob(id: string) {
    return this.http
      .get(`${environment.API_URL}/analytics/processing-jobs/${id}/image`, {
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  getProcessingJobFileBlob(id: string) {
    return this.http
      .get(`${environment.API_URL}/analytics/processing-jobs/${id}/file/serve`, {
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(
        map((blob) => ({ url: URL.createObjectURL(blob), contentType: blob.type })),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  resolveProcessingJobType(jobId: string, documentType: 'StrengthTestResult' | 'DeliveryNote') {
    return this.http
      .patch(`${environment.API_URL}/analytics/processing-jobs/${jobId}/resolve-type`, { documentType }, { observe: 'response' })
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  processJob(jobId: string) {
    return this.http
      .post(`${environment.API_URL}/analytics/process/${jobId}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  parseData(jobId: string) {
    return this.http
      .post(`${environment.API_URL}/analytics/parse/${jobId}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  reviewProcessingJob(jobId: string, payload: any) {
    return this.http
      .post(`${environment.API_URL}/analytics/review/${jobId}`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getLinkSuggestions(jobId: string) {
    return this.http
      .get(`${environment.API_URL}/analytics/link/${jobId}/suggestions`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  linkProcessingJob(jobId: string, payload: any) {
    return this.http
      .post(`${environment.API_URL}/analytics/link/${jobId}`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  /** Unified review method — wraps reviewProcessingJob for use by step components. */
  reviewJob(jobId: string, outputEdited: any, reviewLog: any) {
    return this.http
      .post(`${environment.API_URL}/analytics/review/${jobId}`, { outputEdited, reviewLog }, { observe: 'response' })
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }


  // ==================== AGGREGATION ENDPOINTS ====================

  getConcreteMixtures(projectId: string) {
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/concrete-mixtures/${projectId}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getTestsCount(projectId: string) {
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/tests-count/${projectId}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getProcessingJobsCount(projectId: string, status?: string) {
    const params = status ? `?status=${status}` : '';
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/processing-jobs-count/${projectId}${params}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getPoursCount(projectId: string) {
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/pours-count/${projectId}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getAgeStrengthData(projectId: string, filters?: any) {
    const params = filters?.concreteType
      ? `?concreteType=${filters.concreteType}`
      : '';
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/age-strength/${projectId}${params}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getQualityParameters(projectId: string, parameter: string) {
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/quality-parameters/${projectId}?parameter=${parameter}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getManufacturingTimeline(projectId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : '';
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/manufacturing-timeline/${projectId}${params}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getRecentActivity(projectId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : '';
    return this.http
      .get(
        `${environment.API_URL}/analytics/aggregation/recent-activity/${projectId}${params}`
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  // ==================== DATA EXPLORER ====================

  getDistinctValues(projectId: string, fieldPath: string, isArrayField: boolean = false) {
    return this.http
      .get<Array<{ value: string | number; count: number }>>(
        `${environment.API_URL}/analytics/aggregation/distinct-values/${projectId}`,
        { params: { field: fieldPath, isArrayField: String(isArrayField) } }
      )
      .pipe(
        map((response) => response),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  exploreData(projectId: string, params: {
    dateRange?: { start: Date; end: Date };
    metricField: string;
    aggregation?: 'average' | 'sum' | 'min' | 'max' | 'count';
    groupByField?: string;
    timeAggregation?: 'none' | 'day' | 'week' | 'month' | 'quarter' | 'group';
    isArrayField?: boolean;
    filters?: Array<{
      field: string;
      filterType: 'categorical' | 'numeric';
      values?: (string | number)[];
      operator?: 'gt' | 'lt' | 'eq' | 'neq' | 'gte' | 'lte';
      numericValue?: number;
    }>;
    filterLogic?: 'and' | 'or';
  }) {
    return this.http
      .post(
        `${environment.API_URL}/analytics/aggregation/data-explorer/${projectId}`,
        params
      )
      .pipe(
        map((response) => response),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }
}



